import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BasicModal, Tabs } from '@life-line-lab/components-react';

// ANTD
import { CloseCircleOutlined } from '@ant-design/icons';

// TAB COMPONENTS
import DriverDetails from './profile-tab-contents/driver-details/driver-details';
import VehicleInfo from './profile-tab-contents/vehicle-info/vehicle-info';
import PaymentDetails from './profile-tab-contents/payment-details/payment-details';
import EmergencyContact from './profile-tab-contents/emergency-contact/emergency-contact';
import Attachment from './profile-tab-contents/attachment/attachment';

// HELPERS
import { encode } from 'Helpers/index';

// SCSS
import "./driver-profile.scss";
import { Row } from 'antd';
import TabContentFooter from './profile-tab-footer/tab-content-footer';
import { useHistory } from 'react-router-dom';
import ModalContent from 'Components/shared/modal-content';
import { DriverApprovalStatus, DriverStatus, DriverCategories, DriverCategoriesTabs } from 'Types/new_driver_types';
import { ModalTranslationContent } from 'Types/common.types';

interface DriverProfileProps {
    canEdit:boolean;
    canDelete:boolean;
    driverDetails?:any;
    onBack:()=>void;
    onDeactivateDriver:(profileID:string,toDelete:boolean,reason:string)=>void;
}

const DriverProfile:React.FC<DriverProfileProps> = ({
    canEdit,
    canDelete,
    driverDetails,
    onBack,
    onDeactivateDriver
}) => {

    const { t } = useTranslation();
    const history = useHistory();
    const sharedTabsProps = {
        driverDetails,
    };
    const initialModal = {
        open: false, record: {}, type: "approve" 
    };
    const tabs = [
        {
            tabKey: '0',
            label: t("tab.driverDetails"),
            component: <DriverDetails
                {...sharedTabsProps}
            />
        },
        {
            tabKey: '1',
            label: t("tab.vehicleInformation"),
            component: <VehicleInfo
                {...sharedTabsProps}
            />
        },
        {
            tabKey: '2',
            label: t("tab.paymentDetails"),
            component: <PaymentDetails
                {...sharedTabsProps}
            />
        },
        {
            tabKey: '3',
            label: t("tab.emergencyContactDetails"),
            component: <EmergencyContact
                {...sharedTabsProps}
            />
        },
        {
            tabKey: '4',
            label: t("tab.attachments"),
            component: <Attachment
                {...sharedTabsProps}
            />
        }
    ];
    const editFormType : DriverCategories = [
        DriverCategoriesTabs.DRIVER_INFO,
        DriverCategoriesTabs.VEHICLE_INFO,
        DriverCategoriesTabs.PAYMENT_INFO,
        DriverCategoriesTabs.EMERGENCY_INFO,
        DriverCategoriesTabs.ATTACHMENT_INFO,
    ];

    const [tabActiveKey, setTabActiveKey] = useState<string>("0");
    const [modal, setModal] = useState<any>(initialModal);

    const handleTabClick = (key: string) => {
        setTabActiveKey(key);
    }

    const handleEdit = (tabActiveKey:string) => {
        if (driverDetails.profileID) {
            const driverProfileID = encode(driverDetails.profileID);
            history.push(`/driver-management/driver/${editFormType[parseInt(tabActiveKey)].toLowerCase()}/${driverProfileID}/edit`);
        }
    }

    // ? write as a helper function
    const handleModalTranslation = () : ModalTranslationContent => {
        if (modal.type === DriverApprovalStatus.APPROVED) {
            return {
                title: t("label.confirmDriverApproval").toUpperCase(),
                sureTxt: t("txt.areYouSureApproveAccount"),
                contentToConfirm: t("txt.driverID").toUpperCase(),
                actionLabel: t("action.approve")
            }
        } else if (modal.type === DriverApprovalStatus.REJECTED) {
            return {
                title: t("label.rejectDriver").toUpperCase(),
                sureTxt: t("txt.areYouSureRejectAccount"),
                contentToConfirm: t("txt.driverID").toUpperCase(),
                actionLabel: t("action.reject")
            }
        } else if (modal.type === DriverStatus.DEACTIVATE) {
            return {
                title: t("label.deactivateDriver").toUpperCase(),
                sureTxt: t("txt.areYouSureDeactivateAccount"),
                contentToConfirm: t("txt.driverID").toUpperCase(),
                actionLabel: t("action.deactivate")
            }
        } else if (modal.type === DriverStatus.ACTIVATE) {
            return {
                title: t("label.activateDriver").toUpperCase(),
                sureTxt: t("txt.areYouSureActivateAccount"),
                contentToConfirm: t("txt.driverID").toUpperCase(),
                actionLabel: t("action.activate")
            }
        } else return {
            title: '-',
            sureTxt: '-',
            contentToConfirm: '-',
            actionLabel: '-'
        }
    }

    const handleSetModal = (type) => {
        setModal({
            ...modal, open: true, type: type, 
            record: driverDetails,
        })
    }

    const handleResetModalState = () => {
        setModal(initialModal);
    }

    return (
        <div className="p-20 my-20 driver-profile-container">
            <div className="main-header">
                <span className="header-title">
                    {t("title.id").toUpperCase() + ' ' + driverDetails?.code + ' | '}
                    {
                        driverDetails.status === "PENDING_ACCOUNT" ?
                            t("title.pendingDriver").toUpperCase() : driverDetails.status === "DEACTIVATED" ? 
                            t("title.rejectedDriver").toUpperCase() : 
                            t('title.activeDriver').toUpperCase()
                    }
                </span>
                <CloseCircleOutlined onClick={() => { onBack() }}
                    className="cursor-pointer back-action"
                />
            </div>

            {/* Different tabs of driver profile info */}
            <Tabs
                destroyInactiveTabPane={true}
                onTabClick={handleTabClick}
                defaultActiveKey="0"
                activeKey={tabActiveKey}
                tabBarGutter={25}
                tabs={tabs}
                className="mt-30 p-20"
            />

            {/* Footer */}
            <Row justify='end'>
                <TabContentFooter
                    canEdit={canEdit}
                    canDelete={canDelete}
                    handleEdit={() => handleEdit(tabActiveKey)}
                    handleSetModal={handleSetModal}
                    driverDetails={driverDetails}
                />
            </Row>
            
            {/* Confirm modal */}
            <BasicModal
                isOpen={modal.open}
                isClosable={true}
                isMaskClosable={false}
                onClose={() => {handleResetModalState()}}
            >
                <ModalContent 
                    modal={modal}
                    onHandleModalTranslation={handleModalTranslation}
                    onResetModal={handleResetModalState}
                    onDeactivateOrDelete={onDeactivateDriver}
                />
            </BasicModal>
        </div>
    )
}

export default DriverProfile